/**
 * <no-preselect> Web Component
 *
 * Wraps the .product-form__variants container. Clears all initial option
 * selections so the customer must actively choose every option before
 * adding to cart. Validates on ATC click and shows per-option error messages.
 *
 * Works alongside theme.js ProductVariants -- does not replace or patch it.
 *
 * Usage in Liquid:
 *   <no-preselect>
 *     <div class="product-form__variants" ...>...</div>
 *   </no-preselect>
 */
class NoPreselect extends HTMLElement {
  connectedCallback() {
    // Skip if a variant is already selected via URL (?variant=ID)
    if (window.location.search.indexOf('variant=') !== -1) return;

    this.container = this.querySelector('.product-form__variants');
    if (!this.container) return;

    this.productSection = this.closest('[data-section-id][data-section-type="product"]');
    if (!this.productSection) return;

    this.form = this.closest('form[action*="/cart/add"]');
    if (!this.form) this.form = this.productSection.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.selectOnlyMode = this.container.getAttribute('data-no-preselect-mode') === 'select-only';

    this._clearInitialState();
    this._injectErrorElements();
    this._attachListeners();
  }

  /* ----------------------------------------------------------------
   * 1. Clear all initial selections
   * -------------------------------------------------------------- */
  _clearInitialState() {
    // Clear radio selections (skip hidden single-value options)
    if (!this.selectOnlyMode) {
      this.container.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        var parentOption = radio.closest('.product-form__option');
        if (parentOption && parentOption.classList.contains('hide-form__option')) return;
        radio.checked = false;
      });
    }

    // Reset selects to placeholder
    this.container.querySelectorAll('select.product-form__single-selector').forEach(function (select) {
      var parentOption = select.closest('.product-form__option');
      if (parentOption && parentOption.classList.contains('hide-form__option')) return;
      var placeholder = select.querySelector('option[value=""]');
      if (placeholder) {
        select.selectedIndex = Array.prototype.indexOf.call(select.options, placeholder);
      } else {
        select.selectedIndex = -1;
      }
    });

    // Clear selected-value display text
    var self = this;
    this.container.querySelectorAll('.product-form__selected-value').forEach(function (el) {
      var parentOption = el.closest('.product-form__option');
      if (parentOption && parentOption.classList.contains('hide-form__option')) return;
      if (self.selectOnlyMode && parentOption && parentOption.getAttribute('data-selector-type') !== 'select') return;
      el.textContent = '';
    });

    // Reset hidden master select (name="id")
    var idSelect = this.form.querySelector('select[name="id"]');
    if (idSelect) idSelect.value = '';

    // Keep inventory visible -- show stock for first available variant even
    // before the customer picks options (server-rendered from
    // selected_or_first_available_variant).

    // Remove data-action so theme.js won't process ATC (keep button clickable for validation)
    this.form.querySelectorAll('button.product-form__add-button').forEach(function (btn) {
      btn.removeAttribute('data-action');
    });

    // Hide theme.js warning-atc (we use per-option errors instead)
    var warningAtc = this.productSection.querySelector('.warning-atc');
    if (warningAtc) warningAtc.style.display = 'none';
  }

  /* ----------------------------------------------------------------
   * 2. Inject per-option error elements
   * -------------------------------------------------------------- */
  _injectErrorElements() {
    var container = this.container;
    var self = this;
    container.querySelectorAll('.product-form__option[data-selector-type]').forEach(function (group) {
      if (self.selectOnlyMode && group.getAttribute('data-selector-type') !== 'select') return;

      var position = NoPreselect._getPositionFromGroup(group);
      if (position === null) return;

      if (!container.querySelector('.product-form__option-error[data-option-position="' + position + '"]')) {
        var errorEl = document.createElement('div');
        errorEl.className = 'product-form__option-error';
        errorEl.setAttribute('data-option-position', position);
        errorEl.style.display = 'none';
        errorEl.textContent = self.getAttribute('data-required-message') || 'This is a required field.';
        group.appendChild(errorEl);
      }
    });
  }

  /* ----------------------------------------------------------------
   * 3. Attach event listeners
   * -------------------------------------------------------------- */
  _attachListeners() {
    var self = this;

    // Capture-phase change listener: protect against theme.js crash
    // when variant is null (not all options selected yet)
    this.productSection.addEventListener('change', function (e) {
      var selector = e.target.closest('.product-form__single-selector');
      if (!selector) return;

      var result = self._validateAllOptions();
      if (result.allSelected) return;

      // Not all options selected -- variant will be null, theme.js may crash.
      var errorHandler = function (event) {
        if (event.error instanceof TypeError) {
          event.preventDefault();
        }
      };
      window.addEventListener('error', errorHandler);

      setTimeout(function () {
        window.removeEventListener('error', errorHandler);

        var wAtc = self.productSection.querySelector('.warning-atc');
        if (wAtc) wAtc.style.display = 'none';

        // Restore default price (theme.js hides it when variant is null)
        var priceListEl = self.productSection.querySelector('.price-list');
        if (priceListEl) priceListEl.style.display = '';

        self.form.querySelectorAll('button.product-form__add-button').forEach(function (btn) {
          btn.removeAttribute('data-action');
        });

        // Keep inventory visible during partial selection -- theme.js
        // _updateInventory will refresh it once a full variant is resolved.
      }, 0);
    }, true);

    // Capture-phase click interceptor on ATC buttons
    this.productSection.addEventListener('click', function (e) {
      var atcTarget = e.target.closest('[data-action="add-to-cart"], .product-form__add-button');
      if (!atcTarget) return;
      if (atcTarget.matches('.customize-it-btn, .sticky-customize-it')) return;

      if (self.validateForAtc()) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }, true);

    // Clear error on option change
    this.container.addEventListener('change', function (e) {
      var selector = e.target.closest('.product-form__single-selector');
      if (!selector) return;

      var position = selector.getAttribute('data-option-position');
      if (!position) return;

      var errorEl = self.container.querySelector('.product-form__option-error[data-option-position="' + position + '"]');
      if (errorEl) errorEl.style.display = 'none';

      self._checkAndEnableATC();
    });

    // Click listener for label-based radio interactions (color swatches)
    this.container.addEventListener('click', function (e) {
      var radio = e.target.closest('input[type="radio"].product-form__single-selector');
      if (!radio) {
        var label = e.target.closest('label');
        if (label && label.getAttribute('for')) {
          radio = self.container.querySelector('#' + CSS.escape(label.getAttribute('for')));
        }
      }
      if (!radio || radio.tagName !== 'INPUT') return;

      var position = radio.getAttribute('data-option-position');
      if (!position) return;

      setTimeout(function () {
        var errorEl = self.container.querySelector('.product-form__option-error[data-option-position="' + position + '"]');
        if (errorEl) errorEl.style.display = 'none';
        self._checkAndEnableATC();
      }, 0);
    });
  }

  /* ----------------------------------------------------------------
   * Helpers
   * -------------------------------------------------------------- */

  static _getPositionFromGroup(group) {
    var selector = group.querySelector('[data-option-position]');
    return selector ? selector.getAttribute('data-option-position') : null;
  }

  validateForAtc() {
    // When a variant is supplied in the URL, connectedCallback intentionally
    // leaves this component inactive and the normal theme ATC flow should run.
    if (!this.container) return true;

    var result = this._validateAllOptions();
    if (result.allSelected) return true;

    for (var i = 0; i < result.errors.length; i++) {
      if (result.errors[i].errorEl) result.errors[i].errorEl.style.display = 'block';
    }

    if (result.errors.length > 0 && result.errors[0].group) {
      result.errors[0].group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return false;
  }

  _validateAllOptions() {
    var container = this.container;
    var allSelected = true;
    var errors = [];

    container.querySelectorAll('.product-form__option[data-selector-type]').forEach(function (group) {
      if (group.classList.contains('hide-form__option')) return;

      var selectorType = group.getAttribute('data-selector-type');
      if (this.selectOnlyMode && selectorType !== 'select') return;

      var position = NoPreselect._getPositionFromGroup(group);
      var isSelected = false;

      if (selectorType === 'select') {
        var select = group.querySelector('select.product-form__single-selector');
        isSelected = select && select.value !== '';
      } else {
        isSelected = !!group.querySelector('input[type="radio"].product-form__single-selector:checked');
      }

      var errorEl = container.querySelector('.product-form__option-error[data-option-position="' + position + '"]');

      if (!isSelected) {
        allSelected = false;
        errors.push({ group: group, position: position, errorEl: errorEl });
      } else if (errorEl) {
        errorEl.style.display = 'none';
      }
    }, this);

    return { allSelected: allSelected, errors: errors };
  }

  _checkAndEnableATC() {
    var result = this._validateAllOptions();
    if (!result.allSelected) return;

    this.form.querySelectorAll('button.product-form__add-button').forEach(function (btn) {
      if (!btn.getAttribute('data-action')) {
        btn.setAttribute('data-action', 'add-to-cart');
        btn.removeAttribute('disabled');
      }
    });

    var warningAtc = this.productSection.querySelector('.warning-atc');
    if (warningAtc) warningAtc.style.display = 'none';
  }
}

if (!customElements.get('no-preselect')) {
  customElements.define('no-preselect', NoPreselect);
}
